/**
 * DateRangeSlider formatting + label contract.
 *
 * Pure (non-component) helpers shared between {@link DateRangeSlider} and the
 * SortFilterBar chip summary. Labels follow the mockup's
 * `after / before / range / Any` contract.
 */

const DAY_MS = 86_400_000;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;
export const EPSILON = 1e-6;

/** Resolve a percent position (0 = oldest, 100 = now) to an absolute Date. */
export function percentToDate(percent: number, spanDays: number, now: number): Date {
  const daysAgo = Math.round((1 - percent / 100) * spanDays);
  return new Date(now - daysAgo * DAY_MS);
}

/** Format a percent position as a short `DD Mon` label. */
export function formatDateEndpoint(percent: number, spanDays: number, now: number): string {
  const d = percentToDate(percent, spanDays, now);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/**
 * i18n vocabulary for the date value label. Defaults match the mockup. The
 * wiring slice injects translated `any`/`after`/`before` from `filters.*` keys.
 */
export interface DateRangeLabels {
  any?: string;
  after?: string;
  before?: string;
}

/**
 * The `after / before / range / Any` value label.
 *
 * - both thumbs at the ends → `Any`
 * - low at min only → `before <hi>` (only the upper bound is constrained)
 * - high at max only → `after <lo>`
 * - otherwise → `<lo> – <hi>`
 */
export function formatDateRangeValueLabel(
  loPct: number,
  hiPct: number,
  spanDays: number,
  now: number,
  labels?: DateRangeLabels,
): string {
  const atMin = loPct <= EPSILON;
  const atMax = hiPct >= 100 - EPSILON;
  if (atMin && atMax) return labels?.any ?? 'Any';
  if (atMin) return `${labels?.before ?? 'before'} ${formatDateEndpoint(hiPct, spanDays, now)}`;
  if (atMax) return `${labels?.after ?? 'after'} ${formatDateEndpoint(loPct, spanDays, now)}`;
  return `${formatDateEndpoint(loPct, spanDays, now)} – ${formatDateEndpoint(hiPct, spanDays, now)}`;
}
